// src/services/client-service.js
// CRUD operations for the practitioners/{pid}/clients subcollection.
// Client records are auto-created by the syncBookingToCalendar Cloud Function
// and can also be created manually from the ClientManagement page.

import { db } from '../config/firebase-config';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';

// ---------------------------------------------------------------------------
// getClients — fetch all clients for a practitioner, sorted by lastSessionDate
// ---------------------------------------------------------------------------
export async function getClients(practitionerId) {
  try {
    const col = collection(db, 'practitioners', practitionerId, 'clients');
    // orderBy requires an index; fall back to client-side sort if index missing
    let docs;
    try {
      const q = query(col, orderBy('lastSessionDate', 'desc'));
      const snap = await getDocs(q);
      docs = snap.docs;
    } catch {
      // Index not yet created — fetch unordered and sort client-side
      const snap = await getDocs(col);
      docs = snap.docs;
    }
    return docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (err) {
    console.error('getClients error:', err);
    throw new Error('Failed to load clients');
  }
}

// ---------------------------------------------------------------------------
// getClientByEmail — look up a client by email for deduplication
// ---------------------------------------------------------------------------
export async function getClientByEmail(practitionerId, email) {
  try {
    const col = collection(db, 'practitioners', practitionerId, 'clients');
    const q = query(col, where('email', '==', email.toLowerCase().trim()));
    const snap = await getDocs(q);
    if (snap.empty) return null;
    const d = snap.docs[0];
    return { id: d.id, ...d.data() };
  } catch (err) {
    console.error('getClientByEmail error:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// getClientById — fetch a single client by Firestore document ID
// ---------------------------------------------------------------------------
export async function getClientById(practitionerId, clientId) {
  try {
    const ref = doc(db, 'practitioners', practitionerId, 'clients', clientId);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    console.error('getClientById error:', err);
    throw new Error('Failed to load client');
  }
}

// ---------------------------------------------------------------------------
// createClient — create a new client record; updates existing if email matches
// ---------------------------------------------------------------------------
export async function createClient(practitionerId, clientData) {
  try {
    const email = clientData.email?.toLowerCase().trim();
    if (!email) throw new Error('Email is required');

    // Deduplication check
    const existing = await getClientByEmail(practitionerId, email);
    if (existing) {
      await updateClient(practitionerId, existing.id, {
        firstName: clientData.firstName || existing.firstName,
        lastName: clientData.lastName || existing.lastName,
        phone: clientData.phone || existing.phone,
        notes: clientData.notes || existing.notes,
        displayName: clientData.displayName ||
          `${clientData.firstName || existing.firstName || ''} ${clientData.lastName || existing.lastName || ''}`.trim() || email,
      });
      return { id: existing.id, existed: true };
    }

    const displayName =
      clientData.displayName ||
      `${clientData.firstName || ''} ${clientData.lastName || ''}`.trim() ||
      email;

    const now = serverTimestamp();
    const col = collection(db, 'practitioners', practitionerId, 'clients');
    const docRef = await addDoc(col, {
      displayName,
      firstName: clientData.firstName || '',
      lastName: clientData.lastName || '',
      email,
      phone: clientData.phone || '',
      source: clientData.source || 'manual',
      firstSessionDate: clientData.firstSessionDate || null,
      lastSessionDate: clientData.lastSessionDate || null,
      totalSessions: clientData.totalSessions || 0,
      totalRevenue: clientData.totalRevenue || 0,
      status: 'active',
      notes: clientData.notes || '',
      tags: clientData.tags || [],
      createdAt: now,
      updatedAt: now,
    });

    return { id: docRef.id, existed: false };
  } catch (err) {
    console.error('createClient error:', err);
    throw new Error(err.message || 'Failed to create client');
  }
}

// ---------------------------------------------------------------------------
// updateClient — update specific fields on a client record
// ---------------------------------------------------------------------------
export async function updateClient(practitionerId, clientId, updates) {
  try {
    const ref = doc(db, 'practitioners', practitionerId, 'clients', clientId);
    // Recompute displayName if name fields changed
    if (updates.firstName !== undefined || updates.lastName !== undefined) {
      const existing = await getDoc(ref);
      const data = existing.data() || {};
      const firstName = updates.firstName ?? data.firstName ?? '';
      const lastName = updates.lastName ?? data.lastName ?? '';
      updates.displayName = `${firstName} ${lastName}`.trim() || data.email;
    }
    await updateDoc(ref, { ...updates, updatedAt: serverTimestamp() });
  } catch (err) {
    console.error('updateClient error:', err);
    throw new Error('Failed to update client');
  }
}

// ---------------------------------------------------------------------------
// deleteClient — delete client record + clean up associated data
// Deletes intake responses, archives bookings (for financial records), then removes client doc.
// ---------------------------------------------------------------------------
export async function deleteClient(practitionerId, clientId) {
  try {
    const ref = doc(db, 'practitioners', practitionerId, 'clients', clientId);

    // Get client email for cleanup queries
    const clientSnap = await getDoc(ref);
    const clientEmail = clientSnap.exists() ? clientSnap.data().email?.toLowerCase().trim() : null;

    if (clientEmail) {
      // Delete all intake responses for this client
      try {
        const intakeSnap = await getDocs(
          query(
            collection(db, 'practitioners', practitionerId, 'intakeResponses'),
            where('clientEmail', '==', clientEmail),
          )
        );
        await Promise.all(intakeSnap.docs.map(d => deleteDoc(d.ref)));
      } catch (err) {
        console.error('deleteClient: intake cleanup failed:', err.message);
      }

      // Archive bookings — keep for financial records but hide from active client view
      try {
        const bookingsSnap = await getDocs(
          query(collection(db, 'bookings'), where('practitionerId', '==', practitionerId))
        );
        const clientBookingDocs = bookingsSnap.docs.filter(
          d => d.data().client?.email?.toLowerCase() === clientEmail
        );
        await Promise.all(clientBookingDocs.map(d => updateDoc(d.ref, { clientArchived: true })));
      } catch (err) {
        console.error('deleteClient: booking archive failed:', err.message);
      }
    }

    await deleteDoc(ref);
  } catch (err) {
    console.error('deleteClient error:', err);
    throw new Error('Failed to delete client');
  }
}

// ---------------------------------------------------------------------------
// getClientBookings — fetch all bookings for a specific client email
// Queries by practitionerId only and filters by email client-side
// (avoids composite index requirement on nested client.email field)
// ---------------------------------------------------------------------------
export async function getClientBookings(practitionerId, clientEmail) {
  try {
    const emailLower = clientEmail.toLowerCase().trim();
    const col = collection(db, 'bookings');
    const q = query(col, where('practitionerId', '==', practitionerId));
    const snap = await getDocs(q);
    const bookings = snap.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(b => b.client?.email?.toLowerCase() === emailLower && !b.clientArchived);

    // Sort by dateTime descending (most recent first)
    bookings.sort((a, b) => {
      const aTime = a.dateTime?.toDate ? a.dateTime.toDate().getTime() : new Date(a.dateTime).getTime();
      const bTime = b.dateTime?.toDate ? b.dateTime.toDate().getTime() : new Date(b.dateTime).getTime();
      return bTime - aTime;
    });

    return bookings;
  } catch (err) {
    console.error('getClientBookings error:', err);
    throw new Error('Failed to load bookings');
  }
}
